import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Phone, 
  MapPin, 
  Mail, 
  Menu, 
  X, 
  SprayCan, 
  Wrench, 
  Gem, 
  Circle, 
  Move, 
  Flame,
  Facebook,
  Instagram,
  Twitter,
  ChevronRight,
  Send,
  Clock,
  Award,
  Shield,
  Star,
  Navigation,
  ExternalLink
} from "lucide-react";
import { Toaster, toast } from "sonner";
import AdminPanel from "./AdminPanel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Images - R&O Refurbishment own photos
const IMAGES = {
  hero: "https://customer-assets.emergentagent.com/job_9c7b7101-a5e3-4c24-af08-51b602f81fce/artifacts/kpfhyzp9_IMG_3275.jpg",
  service1: "https://customer-assets.emergentagent.com/job_9c7b7101-a5e3-4c24-af08-51b602f81fce/artifacts/7dj6gdm8_IMG_5204%202.jpeg",
  service2: "https://customer-assets.emergentagent.com/job_9c7b7101-a5e3-4c24-af08-51b602f81fce/artifacts/px3z7ti1_IMG_7207.jpg",
  service3: "https://customer-assets.emergentagent.com/job_9c7b7101-a5e3-4c24-af08-51b602f81fce/artifacts/n5v763xz_IMG_9159.jpg",
  gallery1: "https://customer-assets.emergentagent.com/job_9c7b7101-a5e3-4c24-af08-51b602f81fce/artifacts/9ysfjzpo_IMG_9617%202.jpeg",
  gallery2: "https://customer-assets.emergentagent.com/job_9c7b7101-a5e3-4c24-af08-51b602f81fce/artifacts/kpfhyzp9_IMG_3275.jpg",
  gallery3: "https://customer-assets.emergentagent.com/job_9c7b7101-a5e3-4c24-af08-51b602f81fce/artifacts/7dj6gdm8_IMG_5204%202.jpeg"
};

// Services data
const SERVICES = [
  {
    id: "powder-coating",
    name: "Powder Coating",
    description: "Full powder coating service with custom colors and finishes for a durable, professional look.",
    icon: SprayCan,
    image: IMAGES.service1
  },
  {
    id: "alloy-repair",
    name: "Alloy Repair",
    description: "Professional alloy wheel repair and refurbishment to restore your wheels to their former glory.",
    icon: Wrench,
    image: IMAGES.service2
  },
  {
    id: "powder-coating-2-tones",
    name: "Powder Coating 2 Tones",
    description: "Two-tone powder coating for unique, custom dual-color finishes on your wheels.",
    icon: Gem,
    image: IMAGES.service3
  },
  {
    id: "tire-fitting",
    name: "Tire Fitting",
    description: "Complete tire fitting and balancing service for optimal performance.",
    icon: Circle,
    image: IMAGES.gallery1
  },
  {
    id: "straightening",
    name: "Wheel Straightening",
    description: "Hydraulic wheel straightening for buckled and bent wheels.",
    icon: Move,
    image: IMAGES.gallery2
  },
  {
    id: "welding",
    name: "Wheel Welding",
    description: "Expert TIG welding for cracked alloy wheels, restoring structural integrity.",
    icon: Flame,
    image: IMAGES.gallery3
  }
];

// Gallery data
const GALLERY = [
  { id: 1, image: IMAGES.gallery1, title: "Custom Purple Finish", category: "Powder Coating" },
  { id: 2, image: IMAGES.service2, title: "Two Tone Restoration", category: "Powder Coating 2 Tones" },
  { id: 3, image: IMAGES.hero, title: "Black Gloss Finish", category: "Powder Coating" },
  { id: 4, image: IMAGES.service1, title: "Curb Damage Repair", category: "Alloy Repair" },
  { id: 5, image: IMAGES.service3, title: "Two Tone Custom", category: "Powder Coating" },
  { id: 6, image: IMAGES.gallery3, title: "Full Wheel Restore", category: "Complete Service" }
];

// Navbar Component
const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: "Home", href: "#home" },
    { name: "Services", href: "#services" },
    { name: "Gallery", href: "#gallery" },
    { name: "Contact", href: "#contact" }
  ];

  return (
    <nav className={`navbar ${scrolled ? 'py-3' : 'py-4'}`} data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <a href="#home" className="flex items-center gap-2" data-testid="logo">
            <span className="text-2xl font-bold text-[#7C3AED]" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              R&O
            </span>
            <span className="text-lg text-white uppercase tracking-wider" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              Refurbishment
            </span>
          </a>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                className="nav-link"
                data-testid={`nav-${link.name.toLowerCase()}`}
              >
                {link.name}
              </a>
            ))}
            <a href="#contact" className="btn-primary text-sm" data-testid="nav-quote-btn">
              Get a Quote
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-white p-2"
            onClick={() => setIsOpen(!isOpen)}
            data-testid="mobile-menu-btn"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden"
            >
              <div className="mobile-menu">
                {navLinks.map((link) => (
                  <a
                    key={link.name}
                    href={link.href}
                    className="nav-link py-3"
                    onClick={() => setIsOpen(false)}
                  >
                    {link.name}
                  </a>
                ))}
                <a href="#contact" className="btn-primary text-center mt-4" onClick={() => setIsOpen(false)}>
                  Get a Quote
                </a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section id="home" className="hero-section" data-testid="hero-section">
      <div className="hero-gradient"></div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-20 md:py-0">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
              data-testid="hero-title"
            >
              R&O REFURBISHMENT
            </h1>
            <p 
              className="text-xl md:text-2xl text-[#7C3AED] mb-6 uppercase tracking-wider"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
            >
              Wheel Powder Coating Bathgate
            </p>
            <p className="text-gray-400 text-lg mb-6 max-w-lg">
              Professional alloy wheel refurbishment and powder coating services. 
              Transform your wheels with our expert craftsmanship.
            </p>
            
            {/* Trust Badges */}
            <div className="flex flex-wrap gap-4 mb-8">
              <div className="flex items-center gap-2 bg-[#121212] px-4 py-2 rounded-lg border border-[#27272A]">
                <Award className="text-[#7C3AED]" size={20} />
                <span className="text-sm text-gray-300">7 Years Experience</span>
              </div>
              <div className="flex items-center gap-2 bg-[#121212] px-4 py-2 rounded-lg border border-[#27272A]">
                <Shield className="text-[#7C3AED]" size={20} />
                <span className="text-sm text-gray-300">12 Months Warranty</span>
              </div>
            </div>
            <div className="flex flex-wrap gap-4">
              <a href="#contact" className="btn-primary btn-glow" data-testid="hero-quote-btn">
                Get a Quote
              </a>
              <a href="#services" className="btn-secondary" data-testid="hero-services-btn">
                Our Services
              </a>
            </div>
          </motion.div>

          {/* Right Image */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            <div className="relative rounded-lg overflow-hidden">
              <img
                src={IMAGES.hero}
                alt="Premium Alloy Wheel"
                className="w-full h-auto rounded-lg"
                data-testid="hero-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Services Strip
const ServicesStrip = () => {
  const quickServices = [
    { icon: SprayCan, name: "Powder Coating" },
    { icon: Wrench, name: "Alloy Repair" },
    { icon: Gem, name: "2 Tones Coating" },
    { icon: Circle, name: "Tire Fitting" }
  ];

  return (
    <div className="bg-[#7C3AED] py-6" data-testid="services-strip">
      <div className="max-w-7xl mx-auto px-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {quickServices.map((service, index) => (
            <motion.div
              key={service.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="flex items-center justify-center gap-3 text-white"
            >
              <service.icon size={24} />
              <span 
                className="text-sm md:text-base uppercase font-semibold tracking-wider"
                style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
              >
                {service.name}
              </span>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Services Section
const ServicesSection = () => {
  return (
    <section id="services" className="py-20 bg-[#050505]" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-left mb-12"
        >
          <div className="accent-bar"></div>
          <h2 className="section-header" data-testid="services-title">
            Alloy Wheel Services List
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl">
            Complete range of professional wheel refurbishment services
          </p>
        </motion.div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {SERVICES.map((service, index) => (
            <motion.div
              key={service.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="service-card group cursor-pointer"
              data-testid={`service-card-${service.id}`}
            >
              <div className="flex items-start gap-4">
                <div className="p-3 bg-[#7C3AED]/10 rounded-lg group-hover:bg-[#7C3AED]/20 transition-colors duration-300">
                  <service.icon className="text-[#7C3AED]" size={28} />
                </div>
                <div>
                  <h3 
                    className="text-xl font-bold text-white mb-2 uppercase"
                    style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                  >
                    {service.name}
                  </h3>
                  <p className="text-gray-400 text-sm">
                    {service.description}
                  </p>
                </div>
              </div>
              <div className="mt-4 flex items-center text-[#7C3AED] text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <span className="uppercase tracking-wider" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                  Learn More
                </span>
                <ChevronRight size={16} className="ml-1" />
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Gallery Section
const GallerySection = () => {
  return (
    <section id="gallery" className="py-20 bg-[#0a0a0a]" data-testid="gallery-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-left mb-12"
        >
          <div className="accent-bar"></div>
          <h2 className="section-header" data-testid="gallery-title">
            Our Work Gallery
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl">
            Browse through our recent projects and transformations
          </p>
        </motion.div>

        {/* Gallery Grid */}
        <div className="gallery-grid">
          {GALLERY.map((item, index) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="gallery-item"
              data-testid={`gallery-item-${item.id}`}
            >
              <img src={item.image} alt={item.title} />
              <div className="gallery-overlay">
                <div>
                  <p className="text-[#7C3AED] text-sm uppercase tracking-wider mb-1">
                    {item.category}
                  </p>
                  <h3 
                    className="text-white text-lg font-bold uppercase"
                    style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                  >
                    {item.title}
                  </h3>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    service: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Message sent successfully! We'll get back to you soon.");
      setFormData({ name: "", email: "", phone: "", message: "", service: "" });
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Failed to send message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="contact-section py-20" data-testid="contact-section">
      <div className="contact-gradient"></div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Left - Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <div className="accent-bar"></div>
            <h2 className="section-header" data-testid="contact-title">
              Contact Us
            </h2>
            <p className="text-gray-400 text-lg mb-8">
              Get in touch for a free quote or to discuss your wheel refurbishment needs.
            </p>

            {/* Phone */}
            <div className="space-y-4 mb-6">
              <p className="text-gray-400 uppercase text-sm tracking-wider mb-2">Phone:</p>
              <a href="tel:+447375050452" className="phone-number hover:text-[#7C3AED] transition-colors duration-300" data-testid="phone-mobile">
                <Phone className="text-[#7C3AED]" size={24} />
                +44 7375 050452
              </a>
            </div>

            {/* Email */}
            <div className="flex items-center gap-3 mb-6">
              <Mail className="text-[#7C3AED]" size={24} />
              <a href="mailto:casperek31@gmail.com" className="text-gray-400 hover:text-[#7C3AED] transition-colors duration-300" data-testid="email">
                casperek31@gmail.com
              </a>
            </div>

            {/* Address */}
            <div className="flex items-start gap-3 mb-6">
              <MapPin className="text-[#7C3AED] flex-shrink-0 mt-1" size={24} />
              <div>
                <p className="text-white font-semibold">Address:</p>
                <p className="text-gray-400">
                  R&O Refurbishment<br />
                  Unit 5 Mosshall Industrial Estate<br />
                  Bathgate EH47 7LY, UK
                </p>
                {/* Map Links */}
                <div className="flex gap-3 mt-2">
                  <a 
                    href="https://www.google.com/maps/search/?api=1&query=R%26O+Refurbishment+Unit+5+Mosshall+Industrial+Estate+Bathgate+EH47+7LY" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-[#7C3AED] text-sm hover:underline"
                  >
                    <Navigation size={14} />
                    Google Maps
                  </a>
                  <a 
                    href="https://maps.apple.com/?q=R%26O+Refurbishment+Unit+5+Mosshall+Industrial+Estate+Bathgate+EH47+7LY" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-[#7C3AED] text-sm hover:underline"
                  >
                    <Navigation size={14} />
                    Apple Maps
                  </a>
                </div>
              </div>
            </div>

            {/* Opening Hours */}
            <div className="flex items-start gap-3 mb-6">
              <Clock className="text-[#7C3AED] flex-shrink-0 mt-1" size={24} />
              <div>
                <p className="text-white font-semibold">Opening Hours:</p>
                <p className="text-gray-400 text-sm">
                  Mon: 10:00 AM - 4:30 PM<br />
                  Wed-Fri: 9:30 AM - 5:30 PM<br />
                  Sat-Sun: Closed
                </p>
              </div>
            </div>

            {/* Google Reviews */}
            <a 
              href="https://www.google.com/search?q=R%26O+Refurbishment+Bathgate+reviews" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#121212] px-4 py-3 rounded-lg border border-[#27272A] hover:border-[#7C3AED] transition-colors duration-300"
            >
              <Star className="text-yellow-500" size={20} />
              <span className="text-white font-semibold">See Our Google Reviews</span>
              <ExternalLink className="text-gray-400" size={16} />
            </a>
          </motion.div>

          {/* Right - Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-5">
                <input
                  type="text"
                  name="name"
                  placeholder="Name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="form-input input-glow"
                  data-testid="input-name"
                />
                <input
                  type="email"
                  name="email"
                  placeholder="Email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="form-input input-glow"
                  data-testid="input-email"
                />
              </div>
              <input
                type="tel"
                name="phone"
                placeholder="Phone"
                value={formData.phone}
                onChange={handleChange}
                required
                className="form-input input-glow"
                data-testid="input-phone"
              />
              <select
                name="service"
                value={formData.service}
                onChange={handleChange}
                className="form-input input-glow"
                data-testid="select-service"
              >
                <option value="">Select a Service</option>
                {SERVICES.map((service) => (
                  <option key={service.id} value={service.id}>
                    {service.name}
                  </option>
                ))}
              </select>
              <textarea
                name="message"
                placeholder="Message"
                value={formData.message}
                onChange={handleChange}
                required
                rows={5}
                className="form-input input-glow resize-none"
                data-testid="input-message"
              />
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary btn-glow w-full flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="submit-btn"
              >
                {isSubmitting ? "Sending..." : "Send Message"}
                <Send size={18} />
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="footer py-12" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          {/* Address & Contact */}
          <div className="text-gray-400 text-center md:text-left">
            <p>R&O Refurbishment, Unit 5 Mosshall Industrial Estate, Bathgate EH47 7LY, UK</p>
            <p className="mt-1">Tel: +44 7375 050452 | Email: casperek31@gmail.com</p>
            <p className="mt-1 text-[#7C3AED]">7 Years Experience • 12 Months Warranty</p>
          </div>

          {/* Social Icons */}
          <div className="flex items-center gap-4">
            <a
              href="#"
              className="p-2 bg-[#121212] rounded-full text-gray-400 hover:text-[#7C3AED] hover:bg-[#1E1E1E] transition-colors duration-300"
              data-testid="social-facebook"
            >
              <Facebook size={20} />
            </a>
            <a
              href="#"
              className="p-2 bg-[#121212] rounded-full text-gray-400 hover:text-[#7C3AED] hover:bg-[#1E1E1E] transition-colors duration-300"
              data-testid="social-instagram"
            >
              <Instagram size={20} />
            </a>
            <a
              href="#"
              className="p-2 bg-[#121212] rounded-full text-gray-400 hover:text-[#7C3AED] hover:bg-[#1E1E1E] transition-colors duration-300"
              data-testid="social-twitter"
            >
              <Twitter size={20} />
            </a>
          </div>
        </div>

        {/* Copyright */}
        <div className="mt-8 pt-8 border-t border-[#27272A] text-center">
          <p className="text-gray-500 text-sm">
            © {new Date().getFullYear()} R&O Refurbishment. All Rights Reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

// Home Page Component
const HomePage = () => {
  return (
    <div className="min-h-screen bg-[#050505]">
      <div className="noise-overlay"></div>
      <Toaster 
        position="top-right" 
        toastOptions={{
          style: {
            background: '#121212',
            color: '#F3F4F6',
            border: '1px solid #27272A'
          }
        }}
      />
      <Navbar />
      <main>
        <HeroSection />
        <ServicesStrip />
        <ServicesSection />
        <GallerySection />
        <ContactSection />
      </main>
      <Footer />
    </div>
  );
};

// Main App Component with Routes
function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/admin" element={<AdminPanel />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
